import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  Inbox, Search, ChevronRight, Loader2, RefreshCw,
} from "lucide-react";

const STATUS_LABEL = {
  new: "New",
  in_progress: "In progress",
  resolved: "Resolved",
};

const STATUS_TONE = {
  new: "bg-amber-100 text-amber-900 border-amber-300",
  in_progress: "bg-blue-100 text-blue-900 border-blue-300",
  resolved: "bg-emerald-100 text-emerald-900 border-emerald-300",
};

export default function AdminEnquiriesPage({ superadmin = false }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ new: 0, in_progress: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  // Timestamp of the last successful fetch — shown next to the Refresh
  // button so an admin can tell at a glance whether the panel is stale
  // (helpful when a submission was just made and hasn't landed yet).
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const pageSize = 25;

  // Debounce the search box so we don’t hammer the API on every keypress.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (statusFilter !== "all") params.status = statusFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (debouncedQ) params.q = debouncedQ;
      const r = await api.adminListContactSubmissions(params);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setCounts(r.data?.status_counts || { new: 0, in_progress: 0, resolved: 0 });
      setLastFetchedAt(new Date());
    } catch (err) {
      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail;
      // Louder messaging when the failure is an auth issue so an admin
      // whose token expired sees a clear "please sign in again" prompt
      // rather than the ambiguous "no enquiries" empty state.
      let msg = detail || "Couldn’t load enquiries. Please retry.";
      if (status === 401) {
        msg = "Your admin session has expired. Please sign in again.";
      } else if (status === 403) {
        msg = "You don’t have permission to view enquiries. Sign in with an admin account.";
      } else if (status === 404) {
        msg = "The enquiries endpoint is not available on this environment. If you deployed recently, wait a minute and refresh.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, debouncedQ, page]);

  useEffect(() => { load(); }, [load]);

  // Gentle polling — every 20 seconds — so a freshly-submitted enquiry
  // naturally appears without the admin having to hit Refresh. Kept
  // conservative (20s not 5s) so we don't waste requests. Only polls
  // while the tab is visible; skipping when hidden respects battery.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible" && !loading) load();
    };
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, [load, loading]);

  const categories = useMemo(() => [
    "General Enquiry",
    "Business Partnership",
    "Complaint",
    "Billing Issue",
    "Other",
  ], []);

  const detailBase = superadmin ? "/superadmin/enquiries" : "/admin/enquiries";
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6" data-testid="admin-enquiries-page">
      <Toaster richColors position="top-center" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Inbox className="h-6 w-6 text-black" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" data-testid="admin-enquiries-title">
              Enquiries
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Contact form submissions from the public site.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={`border ${STATUS_TONE.new}`} data-testid="admin-enquiries-count-new">
            New · {counts.new}
          </Badge>
          <Badge className={`border ${STATUS_TONE.in_progress}`} data-testid="admin-enquiries-count-in-progress">
            In progress · {counts.in_progress}
          </Badge>
          <Badge className={`border ${STATUS_TONE.resolved}`} data-testid="admin-enquiries-count-resolved">
            Resolved · {counts.resolved}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load()}
            disabled={loading}
            data-testid="admin-enquiries-refresh"
            title={lastFetchedAt ? `Last checked ${lastFetchedAt.toLocaleTimeString()}` : ""}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {lastFetchedAt && !loading && !error && (
        <p className="text-[11px] text-muted-foreground -mt-2" data-testid="admin-enquiries-last-fetched">
          Last checked at {lastFetchedAt.toLocaleTimeString()} · auto-refreshing every 20s
        </p>
      )}

      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
              placeholder="Search name, email, or message…"
              className="pl-9"
              data-testid="admin-enquiries-search"
            />
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={(v) => { setPage(1); setStatusFilter(v); }}>
              <SelectTrigger className="w-full md:w-[160px]" data-testid="admin-enquiries-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={(v) => { setPage(1); setCategoryFilter(v); }}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="admin-enquiries-category-filter">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3" data-testid="admin-enquiries-loading">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : error ? (
          <div className="p-8 text-center text-sm text-destructive" data-testid="admin-enquiries-error">
            {error}
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center" data-testid="admin-enquiries-empty">
            <Inbox className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">
              No enquiries match your filters yet.
            </p>
            {total === 0 && (statusFilter === "all" && categoryFilter === "all" && !debouncedQ) && (
              <p className="mt-2 text-xs text-muted-foreground max-w-md mx-auto">
                Submissions from the public{" "}
                <a href="/contact" className="underline hover:text-foreground" target="_blank" rel="noreferrer">/contact</a>{" "}
                page appear here. If you just submitted one, this list auto-refreshes every 20 seconds — or click{" "}
                <button
                  onClick={() => load()}
                  className="underline hover:text-foreground"
                  data-testid="admin-enquiries-empty-refresh"
                >
                  Refresh now
                </button>.
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <Table data-testid="admin-enquiries-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Submitted</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead className="w-[60px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow
                      key={it.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`${detailBase}/${it.id}`)}
                      data-testid={`admin-enquiries-row-${it.id}`}
                    >
                      <TableCell className="text-xs text-muted-foreground align-top pt-3">
                        {formatDate(it.submitted_at)}
                      </TableCell>
                      <TableCell className="align-top pt-3">
                        <p className="font-semibold text-sm">{it.name}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[200px]">{it.email}</p>
                      </TableCell>
                      <TableCell className="align-top pt-3">
                        <Badge variant="outline" className="text-xs">{it.category}</Badge>
                      </TableCell>
                      <TableCell className="align-top pt-3">
                        <p className="text-sm text-muted-foreground line-clamp-2 max-w-[380px]">
                          {it.message}
                        </p>
                      </TableCell>
                      <TableCell className="align-top pt-3">
                        <Badge className={`border ${STATUS_TONE[it.status] || STATUS_TONE.new}`}>
                          {STATUS_LABEL[it.status] || it.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="align-top pt-3 text-right">
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Mobile stacked list */}
            <div className="md:hidden divide-y">
              {items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => navigate(`${detailBase}/${it.id}`)}
                  className="w-full text-left px-4 py-4 hover:bg-muted/40 flex items-start gap-3"
                  data-testid={`admin-enquiries-row-mobile-${it.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{it.name}</span>
                      <Badge className={`border text-[10px] ${STATUS_TONE[it.status] || STATUS_TONE.new}`}>
                        {STATUS_LABEL[it.status] || it.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{it.email}</p>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{it.category}</Badge>
                      <span className="text-[10px] text-muted-foreground">{formatDate(it.submitted_at)}</span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{it.message}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30 text-xs text-muted-foreground">
              <span>
                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="admin-enquiries-prev"
                >
                  Prev
                </Button>
                <span>Page {page} of {totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="admin-enquiries-next"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}
